import { useStoreLogger } from '../logger.ts'
import { GetCachedProductDetails } from './SkuService.ts'
import type { IAoeCartItem } from '../types/CartTypes'

/**
 * Checks the available quantity for a SKU, taking into account any current cart quantity
 * @param orderTypeId The order type ID
 * @param skuCode The SKU code to check
 * @param currentCartQty The current quantity in the cart (defaults to 0)
 * @returns The available quantity that can be added to the cart
 */
export async function GetAvailableQty(orderTypeId: string, skuCode: string, currentCartQty: number = 0): Promise<number> {
  const logger = useStoreLogger()
  logger.debug('GetAvailableQty', orderTypeId, skuCode, currentCartQty)

  const productDetails = await GetCachedProductDetails(
    orderTypeId,
    [skuCode],
  )
  logger.debug('Product details', productDetails)
  if (productDetails == null ||
      productDetails.length === 0 ||
      productDetails[0].skus == null ||
      productDetails[0].skus.length === 0 ||
      productDetails[0].skus[0].inventoryLevel === undefined) {
    return 0;
  }

  const sku = productDetails[0].skus[0]
  const inventoryLevel = sku.inventoryLevel

  // If OutOfStockVisible is true, ignore inventory level checks and make available
  if (sku.outOfStockVisible === true) {
    logger.debug('OutOfStockVisible is true, ignoring inventory level checks')
    // No quantity limit applies, so never block any requested quantity
    return Number.MAX_SAFE_INTEGER;
  }

  // Otherwise, check inventory level normally
  if (inventoryLevel.inventoryLevel == 'OutOfStock' ||
      inventoryLevel.inventoryLevel == 'StopSell') {
    return 0;
  }

  return inventoryLevel.availableQty - currentCartQty;
}

/**
 * Checks the available quantity for a cart item
 * @param orderTypeId The order type ID
 * @param cartItem The cart item to check
 * @param currentCartQty The current quantity in the cart (defaults to 0)
 * @returns The available quantity that can be added to the cart
 */
export async function GetAvailableQtyForCartItem(orderTypeId: string, cartItem: IAoeCartItem, currentCartQty: number = 0): Promise<number> {
  return GetAvailableQty(orderTypeId, cartItem.skuCode, currentCartQty);
}
